import React, { useMemo, useState } from 'react';
import {
  AlertCircle,
  Award,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  ClipboardCheck,
  FileText,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Star
} from 'lucide-react';
import ModelSelector from '../../selectors/ModelSelector';
import {
  createGuideQualityFingerprint,
  guideQualityService,
  validateGuideForQualityEvaluation
} from '../../services/guideQualityService';
import {
  GUIDE_QUALITY_BENCHMARKS,
  GUIDE_QUALITY_RUBRIC,
  getGuideQualityCriterion
} from './guideQualityRubric';

const RESULT_THEMES = {
  emerald: {
    panel: 'border-emerald-200 bg-emerald-50',
    score: 'text-emerald-700',
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    bar: 'bg-emerald-500'
  },
  green: {
    panel: 'border-green-200 bg-green-50',
    score: 'text-green-700',
    badge: 'bg-green-100 text-green-800 border-green-200',
    bar: 'bg-green-500'
  },
  blue: {
    panel: 'border-blue-200 bg-blue-50',
    score: 'text-blue-700',
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
    bar: 'bg-blue-500'
  },
  amber: {
    panel: 'border-amber-200 bg-amber-50',
    score: 'text-amber-700',
    badge: 'bg-amber-100 text-amber-800 border-amber-200',
    bar: 'bg-amber-500'
  },
  red: {
    panel: 'border-red-200 bg-red-50',
    score: 'text-red-700',
    badge: 'bg-red-100 text-red-800 border-red-200',
    bar: 'bg-red-500'
  }
};

const PRIORITY_STYLES = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-blue-100 text-blue-700 border-blue-200'
};

const formatEvaluationDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

const ScoreStars = ({ rating }) => {
  const roundedRating = Math.round(Number(rating) || 0);

  return (
    <div className="flex items-center gap-1" aria-label={`${Number(rating || 0).toFixed(1)} out of 5 stars`}>
      <div className="flex" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${star <= roundedRating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
      <span className="text-sm font-semibold text-gray-700">{Number(rating || 0).toFixed(1)}/5</span>
    </div>
  );
};

const RubricOverview = ({ expanded, onToggle }) => (
  <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between gap-4 p-5 text-left hover:bg-gray-50"
      aria-expanded={expanded}
    >
      <div>
        <h3 className="font-semibold text-gray-900">Scoring methodology</h3>
        <p className="mt-1 text-sm text-gray-600">
          Seven criteria produce a deterministic weighted score out of 100.
        </p>
      </div>
      {expanded
        ? <ChevronUp className="h-5 w-5 flex-shrink-0 text-gray-500" />
        : <ChevronDown className="h-5 w-5 flex-shrink-0 text-gray-500" />}
    </button>

    {expanded && (
      <div className="border-t border-gray-200 p-5">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {GUIDE_QUALITY_RUBRIC.map((criterion) => (
            <div key={criterion.id} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="flex items-start justify-between gap-3">
                <h4 className="font-medium text-gray-900">{criterion.label}</h4>
                <span className="rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-bold text-indigo-700">
                  {criterion.weight}%
                </span>
              </div>
              <p className="mt-2 text-sm leading-5 text-gray-600">{criterion.description}</p>
            </div>
          ))}
        </div>

        <div className="mt-5 rounded-lg border border-gray-200 bg-white p-4">
          <h4 className="text-sm font-semibold text-gray-800">Criterion score benchmarks</h4>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            {GUIDE_QUALITY_BENCHMARKS.map((benchmark) => (
              <div key={benchmark.range} className="rounded-md bg-gray-50 p-3">
                <p className="text-sm font-bold text-gray-800">{benchmark.range} · {benchmark.label}</p>
                <p className="mt-1 text-xs leading-4 text-gray-500">{benchmark.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    )}
  </section>
);

const CriteriaBreakdown = ({ criteria, theme }) => (
  <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
    <div className="mb-5">
      <h3 className="text-lg font-semibold text-gray-900">Score breakdown</h3>
      <p className="mt-1 text-sm text-gray-500">
        Each criterion is scored from 0–10, then multiplied by its published weight.
      </p>
    </div>

    <div className="space-y-5">
      {criteria.map((criterion) => (
        <article key={criterion.id}>
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div>
              <h4 className="font-medium text-gray-900">{criterion.label}</h4>
              <p className="text-xs text-gray-500">
                {criterion.weight}% weight · {criterion.weightedScore}/{criterion.weight} weighted points
              </p>
            </div>
            <p className="text-lg font-bold text-gray-900">{criterion.score.toFixed(1)}<span className="text-sm text-gray-400">/10</span></p>
          </div>

          <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-gray-100">
            <div
              className={`h-full rounded-full ${theme.bar}`}
              style={{ width: `${Math.max(0, Math.min(100, criterion.score * 10))}%` }}
            />
          </div>

          <p className="mt-2 text-sm leading-6 text-gray-700">{criterion.rationale}</p>

          {criterion.evidence.length > 0 && (
            <ul className="mt-2 space-y-1.5">
              {criterion.evidence.map((evidence, index) => (
                <li key={`${criterion.id}_evidence_${index}`} className="flex gap-2 text-xs leading-5 text-gray-600">
                  <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gray-400" />
                  <span><strong className="font-semibold text-gray-700">{evidence.stop}:</strong> {evidence.observation}</span>
                </li>
              ))}
            </ul>
          )}
        </article>
      ))}
    </div>
  </section>
);

const SuggestionsList = ({ suggestions }) => (
  <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
    <div className="flex items-center gap-2">
      <Sparkles className="h-5 w-5 text-indigo-600" />
      <h3 className="text-lg font-semibold text-gray-900">Prioritized improvements</h3>
    </div>
    <p className="mt-1 text-sm text-gray-500">Work from top to bottom for the greatest quality gain.</p>

    {suggestions.length > 0 ? (
      <ul className="mt-5 space-y-4">
        {suggestions.map((suggestion) => {
          const criterion = getGuideQualityCriterion(suggestion.criterionId);
          return (
            <li key={suggestion.id} className="flex gap-3 rounded-lg border border-gray-200 p-4">
              <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-indigo-500" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${PRIORITY_STYLES[suggestion.priority]}`}>
                    {suggestion.priority}
                  </span>
                  {criterion && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                      {criterion.shortLabel}
                    </span>
                  )}
                </div>
                <p className="mt-2 font-medium leading-6 text-gray-900">{suggestion.suggestion}</p>
                {suggestion.whyItMatters && (
                  <p className="mt-1 text-sm leading-6 text-gray-600">{suggestion.whyItMatters}</p>
                )}
                {suggestion.affectedStops.length > 0 && (
                  <p className="mt-2 text-xs text-gray-500">
                    <span className="font-semibold">Applies to:</span> {suggestion.affectedStops.join(', ')}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    ) : (
      <div className="mt-5 rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
        The assessment did not return specific suggestions. Re-run it to request a fresh review.
      </div>
    )}
  </section>
);

const GuideQualityEvaluator = ({
  itinerary,
  selectedModel,
  onModelChange,
  evaluation: controlledEvaluation,
  onEvaluationChange
}) => {
  const guideIdentity = [
    itinerary?.metadata?.id,
    itinerary?.metadata?.filename,
    itinerary?.metadata?.name
  ].filter(Boolean).join('::') || 'untitled-guide';
  const [internalEvaluation, setInternalEvaluation] = useState(null);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [error, setError] = useState('');
  const [rubricExpanded, setRubricExpanded] = useState(true);
  const [reviewContextDraft, setReviewContextDraft] = useState(() => ({
    guideIdentity,
    clientBrief: (
      controlledEvaluation?.guideId
        ? String(controlledEvaluation.guideId) === String(itinerary?.metadata?.id || '')
        : controlledEvaluation?.guideName === (itinerary?.metadata?.name || 'Untitled Guide')
    )
      ? controlledEvaluation?.reviewContext?.clientBrief || ''
      : ''
  }));
  const evaluation = controlledEvaluation === undefined
    ? internalEvaluation
    : controlledEvaluation;
  const clientBrief = reviewContextDraft.guideIdentity === guideIdentity
    ? reviewContextDraft.clientBrief
    : '';
  const clientName = itinerary?.metadata?.clientName
    || itinerary?.metadata?.client_name
    || '';
  const reviewContext = useMemo(
    () => ({ clientBrief, clientName }),
    [clientBrief, clientName]
  );

  const validation = useMemo(
    () => validateGuideForQualityEvaluation(itinerary),
    [itinerary]
  );
  const currentFingerprint = useMemo(
    () => createGuideQualityFingerprint(itinerary, reviewContext),
    [itinerary, reviewContext]
  );
  const isStale = Boolean(
    evaluation?.guideFingerprint
    && currentFingerprint
    && evaluation.guideFingerprint !== currentFingerprint
  );
  const theme = RESULT_THEMES[evaluation?.ratingColor] || RESULT_THEMES.blue;
  const evaluatedAt = formatEvaluationDate(evaluation?.evaluatedAt);

  const updateEvaluation = (nextEvaluation) => {
    if (controlledEvaluation === undefined) {
      setInternalEvaluation(nextEvaluation);
    }
    onEvaluationChange?.(nextEvaluation);
  };

  const handleEvaluate = async () => {
    if (!validation.valid || isEvaluating) return;

    setError('');
    setIsEvaluating(true);
    try {
      const result = await guideQualityService.evaluateGuide(
        itinerary,
        selectedModel,
        reviewContext
      );
      updateEvaluation(result);
      setRubricExpanded(false);
    } catch (evaluationError) {
      console.error('Guide quality evaluation failed:', evaluationError);
      setError(evaluationError.message || 'The quality evaluation failed. Please try again.');
    } finally {
      setIsEvaluating(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="overflow-hidden rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-purple-50 shadow-sm">
        <div className="grid gap-6 p-6 lg:grid-cols-[minmax(0,1fr),360px] lg:p-8">
          <div>
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-indigo-600 p-3 text-white shadow-sm">
                <ClipboardCheck className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-indigo-600">Editorial QA</p>
                <h2 className="text-2xl font-bold text-gray-900">Guide Quality Review</h2>
              </div>
            </div>
            <p className="mt-4 max-w-3xl text-sm leading-6 text-gray-600">
              Evaluate the current guide against a consistent editorial rubric. The AI supplies evidence and recommendations;
              the builder calculates the weighted score and publication rating.
            </p>

            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-white bg-white/80 p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Guide</p>
                <p className="mt-1 truncate font-semibold text-gray-900">{itinerary?.metadata?.name || 'Untitled Guide'}</p>
              </div>
              <div className="rounded-lg border border-white bg-white/80 p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Stops</p>
                <p className="mt-1 font-semibold text-gray-900">{validation.totalStops || itinerary?.points?.length || 0}</p>
              </div>
              <div className="rounded-lg border border-white bg-white/80 p-3">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">In-depth stops</p>
                <p className="mt-1 font-semibold text-gray-900">{validation.stopsWithNarration || 0}</p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <ModelSelector
              selectedModel={selectedModel}
              onModelChange={onModelChange}
              disabled={isEvaluating}
            />

            <button
              type="button"
              onClick={handleEvaluate}
              disabled={!validation.valid || isEvaluating}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {isEvaluating ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Evaluating guide…
                </>
              ) : evaluation ? (
                <>
                  <RefreshCw className="h-5 w-5" />
                  {isStale ? 'Evaluate updated guide' : 'Run evaluation again'}
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5" />
                  Evaluate guide quality
                </>
              )}
            </button>

            {!validation.valid && (
              <div className="mt-3 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <p>{validation.message}</p>
              </div>
            )}

            <p className="mt-3 text-xs leading-5 text-gray-500">
              The LLM reviews only the ordered in-depth narration. Other relevant fields are shared as presence indicators
              for completeness checks. The review does not modify guide content.
            </p>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-indigo-50 p-2 text-indigo-600">
            <FileText className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-semibold text-gray-900">Evaluation context</h3>
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">Optional</span>
            </div>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              Add a client or editorial brief to evaluate objectives, brand voice, required topics, sensitivities,
              and content boundaries. Leave it blank for a standard audience-focused review.
            </p>

            <label htmlFor="guide-quality-client-brief" className="mt-4 block text-sm font-medium text-gray-800">
              Client / editorial brief
            </label>
            <textarea
              id="guide-quality-client-brief"
              value={clientBrief}
              onChange={(event) => setReviewContextDraft({
                guideIdentity,
                clientBrief: event.target.value
              })}
              disabled={isEvaluating}
              maxLength={12000}
              rows={4}
              placeholder="Example: Audience, purpose, desired tone, must-include subjects, claims or language to avoid, institutional sensitivities, and approval requirements."
              className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm leading-6 text-gray-800 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 disabled:bg-gray-100"
            />
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
              <span>
                Audience & client alignment is always scored; client-specific requirements are used only when supplied.
              </span>
              <span>{clientBrief.length.toLocaleString()}/12,000</span>
            </div>

            {itinerary?.metadata?.b2bClient && !clientBrief.trim() && (
              <div className="mt-3 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-800">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <p>This guide is marked as B2B. Add the client brief for a meaningful client-alignment score.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {error && (
        <div className="flex gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-800" role="alert">
          <CircleAlert className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Evaluation failed</p>
            <p className="mt-1 text-sm">{error}</p>
          </div>
        </div>
      )}

      {isEvaluating && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-6 text-center" aria-live="polite">
          <Loader2 className="mx-auto h-9 w-9 animate-spin text-indigo-600" />
          <p className="mt-3 font-semibold text-indigo-900">Reviewing all in-depth narrations</p>
          <p className="mt-1 text-sm text-indigo-700">Scoring all seven criteria and preparing prioritized suggestions.</p>
        </div>
      )}

      {evaluation && isStale && !isEvaluating && (
        <div className="flex gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <div>
            <p className="font-semibold">This result is out of date</p>
            <p className="mt-1 text-sm">
              The guide content has changed since this review. The previous score is shown for reference; evaluate the updated guide before using it for approval.
            </p>
          </div>
        </div>
      )}

      {evaluation && (
        <>
          <section className={`rounded-2xl border p-6 shadow-sm ${theme.panel} ${isStale ? 'opacity-75' : ''}`}>
            <div className="grid items-center gap-6 md:grid-cols-[220px,minmax(0,1fr)]">
              <div className="text-center md:border-r md:border-current md:border-opacity-10">
                <Award className={`mx-auto h-8 w-8 ${theme.score}`} />
                <p className={`mt-2 text-5xl font-black tracking-tight ${theme.score}`}>
                  {evaluation.overallScore.toFixed(1)}
                </p>
                <p className="text-sm font-medium text-gray-500">out of 100</p>
                <div className="mt-3 flex justify-center">
                  <ScoreStars rating={evaluation.starRating} />
                </div>
              </div>

              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full border px-3 py-1 text-sm font-bold ${theme.badge}`}>
                    {evaluation.rating}
                  </span>
                  <span className="text-sm font-medium text-gray-600">{evaluation.readiness}</span>
                </div>
                <h3 className="mt-3 text-xl font-semibold text-gray-900">
                  {evaluation.guideName || 'Guide quality assessment'}
                </h3>
                <p className="mt-2 leading-7 text-gray-700">{evaluation.summary}</p>
                <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                  {evaluatedAt && <span>Evaluated {evaluatedAt}</span>}
                  {evaluation.modelId && <span>Model: {evaluation.modelId}</span>}
                  {evaluation.coverage && (
                    <span>
                      {evaluation.coverage.totalStops} stop{evaluation.coverage.totalStops === 1 ? '' : 's'} included
                    </span>
                  )}
                </div>
              </div>
            </div>
          </section>

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr),minmax(380px,0.85fr)]">
            <CriteriaBreakdown criteria={evaluation.criteria} theme={theme} />
            <div className="space-y-6">
              <SuggestionsList suggestions={evaluation.suggestions} />

              {evaluation.strengths.length > 0 && (
                <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 shadow-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <h3 className="font-semibold text-emerald-950">What is already working</h3>
                  </div>
                  <ul className="mt-4 space-y-2">
                    {evaluation.strengths.map((strength, index) => (
                      <li key={`strength_${index}`} className="flex gap-2 text-sm leading-6 text-emerald-900">
                        <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-500" />
                        <span>{strength}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {evaluation.verificationNotes.length > 0 && (
                <section className="rounded-xl border border-amber-200 bg-amber-50 p-5 shadow-sm">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-amber-700" />
                    <h3 className="font-semibold text-amber-950">Claims to verify</h3>
                  </div>
                  <p className="mt-1 text-xs leading-5 text-amber-800">
                    These are verification prompts, not declarations that the guide is wrong.
                  </p>
                  <ul className="mt-4 space-y-2">
                    {evaluation.verificationNotes.map((note, index) => (
                      <li key={`verification_${index}`} className="flex gap-2 text-sm leading-6 text-amber-900">
                        <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-500" />
                        <span>{note}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {evaluation.coverage?.truncatedStops > 0 && (
                <section className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-xs leading-5 text-gray-600">
                  <strong>Coverage note:</strong> All stops were included, but {evaluation.coverage.truncatedStops === 1
                    ? 'one unusually long in-depth narration was'
                    : `${evaluation.coverage.truncatedStops} unusually long in-depth narrations were`} shortened to fit the model context.
                </section>
              )}
            </div>
          </div>
        </>
      )}

      <RubricOverview
        expanded={rubricExpanded}
        onToggle={() => setRubricExpanded((current) => !current)}
      />
    </div>
  );
};

export default GuideQualityEvaluator;
